import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useObrasFotos } from '@/hooks/useFotos';
import { useSubPartidas } from '@/hooks/useSubPartidas';
import { encolarFotos, iniciarProcesadorFotos } from '@/lib/colaFotos';
import { cn } from '@/lib/utils';
import type { ObraFotos, VinculoFoto } from '@adelante/shared';
import { Buildings, MagnifyingGlass } from '@phosphor-icons/react';
import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CamaraRafaga, type Captura } from './CamaraRafaga';

/**
 * Cámara de fotos de obra disponible en toda la app:
 *
 *   const abrirCamara = useCamaraFotos();
 *   abrirCamara({ obra: 'VC-F.04', sub_partida_id: 12, etiqueta: 'VC-F.04 · Colado Losa' });
 *   abrirCamara();   // captura libre: al terminar se elige la obra
 *   abrirCamara({ vinculo: { registro_id, punto_id, momento: 'hallazgo' }, etiqueta });
 *
 * Las fotos se encolan (colaFotos) y se suben en segundo plano.
 */

export interface DestinoFotos {
  obra?: string;
  sub_partida_id?: number | null;
  /** Texto del destino para la cámara y los avisos. */
  etiqueta?: string;
  /** Vínculo completo (ej. punto de una ficha de control); tiene prioridad sobre `obra`. */
  vinculo?: VinculoFoto;
}

const Ctx = createContext<(d?: DestinoFotos) => void>(() => undefined);

export function useCamaraFotos() {
  return useContext(Ctx);
}

export function CamaraFotosProvider({ children }: { children: ReactNode }) {
  const [destino, setDestino] = useState<DestinoFotos | null>(null);
  const [sinDestino, setSinDestino] = useState<Captura[] | null>(null);

  useEffect(() => iniciarProcesadorFotos(), []);

  async function guardar(capturas: Captura[], vinculo: VinculoFoto, etiqueta: string) {
    await encolarFotos(
      capturas.map(({ foto, tomada_en, ubicacion }) => ({ foto, tomada_en, ubicacion })),
      vinculo,
      etiqueta,
    );
    for (const c of capturas) URL.revokeObjectURL(c.vista);
    const n = capturas.length;
    toast.success(`${n} foto${n === 1 ? '' : 's'} guardada${n === 1 ? '' : 's'}`, {
      description: `${etiqueta} — se suben en segundo plano`,
    });
  }

  function onListo(capturas: Captura[]) {
    const d = destino;
    setDestino(null);
    if (capturas.length === 0) return;
    if (d?.vinculo) void guardar(capturas, d.vinculo, d.etiqueta ?? 'Ficha de control');
    else if (d?.obra)
      void guardar(
        capturas,
        { obra_codigo: d.obra, sub_partida_id: d.sub_partida_id ?? null, momento: 'general' },
        d.etiqueta ?? d.obra,
      );
    else setSinDestino(capturas);
  }

  return (
    <Ctx.Provider value={(d) => setDestino(d ?? {})}>
      {children}
      {destino && (
        <CamaraRafaga
          destino={destino.etiqueta ?? destino.obra}
          onListo={onListo}
          onCerrar={() => setDestino(null)}
        />
      )}
      {sinDestino && (
        <ElegirObraDialog
          capturas={sinDestino}
          onCancelar={() => {
            if (!window.confirm(`¿Descartar ${sinDestino.length} foto(s)?`)) return;
            for (const c of sinDestino) URL.revokeObjectURL(c.vista);
            setSinDestino(null);
          }}
          onElegir={(d) => {
            const caps = sinDestino;
            setSinDestino(null);
            void guardar(
              caps,
              { obra_codigo: d.obra, sub_partida_id: d.sub_partida_id, momento: 'general' },
              d.etiqueta,
            );
          }}
        />
      )}
    </Ctx.Provider>
  );
}

// ============================================================================
// Elegir obra (captura libre)
// ============================================================================

function ElegirObraDialog({
  capturas,
  onCancelar,
  onElegir,
}: {
  capturas: Captura[];
  onCancelar: () => void;
  onElegir: (d: { obra: string; sub_partida_id: number | null; etiqueta: string }) => void;
}) {
  const [texto, setTexto] = useState('');
  const [q, setQ] = useState('');
  const [obra, setObra] = useState<ObraFotos | null>(null);
  const [sub, setSub] = useState<number | null>(null);
  const obrasQ = useObrasFotos(q);
  const subsQ = useSubPartidas();

  useEffect(() => {
    const t = setTimeout(() => setQ(texto.trim()), 300);
    return () => clearTimeout(t);
  }, [texto]);

  const subs = useMemo(
    () =>
      (subsQ.data?.data ?? [])
        .filter((s) => s.activo)
        .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true })),
    [subsQ.data],
  );
  const subSel = subs.find((s) => s.id === sub);

  return (
    <Dialog open onOpenChange={(o) => !o && onCancelar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>¿A qué obra van las fotos?</DialogTitle>
          <DialogDescription>
            {capturas.length} foto{capturas.length === 1 ? '' : 's'} capturada
            {capturas.length === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {capturas.map((c) => (
            <img
              key={c.vista}
              src={c.vista}
              alt=""
              className="h-14 w-14 shrink-0 rounded object-cover"
            />
          ))}
        </div>

        {!obra ? (
          <>
            <div className="relative">
              <MagnifyingGlass
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                weight="bold"
                aria-hidden="true"
              />
              <Input
                autoFocus
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Buscar obra o centro de costo…"
                className="pl-8"
              />
            </div>
            <ul className="max-h-[40vh] space-y-1 overflow-y-auto">
              {obrasQ.isLoading && (
                <li className="py-4 text-center text-sm text-muted-foreground">Cargando…</li>
              )}
              {(obrasQ.data ?? []).slice(0, 60).map((o) => (
                <li key={o.codigo}>
                  <button
                    type="button"
                    onClick={() => setObra(o)}
                    disabled={!o.habilitada}
                    title={o.habilitada ? undefined : 'No admite fotos nuevas'}
                    className="flex w-full items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-left hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Buildings
                      className="h-4 w-4 shrink-0 text-muted-foreground"
                      weight="bold"
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-sm font-semibold">{o.codigo}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {o.nombre ?? o.area_costeo}
                      </span>
                    </span>
                    {!o.habilitada ? (
                      <span className="shrink-0 text-[11px] text-red-700">
                        {o.motivo === 'entregada' ? 'Entregada' : 'Sin fotos nuevas'}
                      </span>
                    ) : (
                      o.n_fotos > 0 && (
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {o.n_fotos} fotos
                        </span>
                      )
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border border-adelante-primary bg-adelante-primary/10 px-3 py-2">
              <span>
                <span className="block font-mono text-sm font-semibold">{obra.codigo}</span>
                <span className="block text-xs text-muted-foreground">{obra.nombre}</span>
              </span>
              <button
                type="button"
                className="text-xs font-medium underline"
                onClick={() => setObra(null)}
              >
                Cambiar
              </button>
            </div>
            {obra.es_vivienda && (
              <label className="block space-y-1 text-sm">
                <span className="font-medium">Sub-partida (opcional)</span>
                <select
                  className={cn(
                    'h-9 w-full rounded-md border border-input bg-background px-2 text-sm',
                  )}
                  value={sub ?? ''}
                  onChange={(e) => setSub(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Sin sub-partida (se puede asignar después)</option>
                  {subs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.codigo} {s.nombre}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancelar}>
            Descartar
          </Button>
          <Button
            disabled={!obra}
            onClick={() =>
              obra &&
              onElegir({
                obra: obra.codigo,
                sub_partida_id: sub,
                etiqueta: subSel ? `${obra.codigo} · ${subSel.nombre}` : obra.codigo,
              })
            }
          >
            Guardar {capturas.length} foto{capturas.length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
